import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexWrap: "wrap"
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1
  },
  btnWrapper: {
    position: "relative"
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12
  }
}));

const initialState = {
  tagAction: "add",
  tagId: "",
  tagName: "",
  tagColor: "#A4CCCC",
  onMissingTag: "skip"
};

const actionOptions = [
  { value: "add", label: "Adicionar tag" },
  { value: "remove", label: "Remover tag" }
];

const FlowBuilderAddTagModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [tags, setTags] = useState([]);
  const [tagAction, setTagAction] = useState("add");
  const [selectedTagId, setSelectedTagId] = useState("");
  const [newTagName, setNewTagName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [missingTagReference, setMissingTagReference] = useState(false);

  const loadTags = async () => {
    const { data: tagsData } = await api.get("/tags/list", {
      params: { kanban: 0 }
    });

    return Array.isArray(tagsData) ? tagsData : [];
  };

  useEffect(() => {
    isMounted.current = true;

    const bootstrapModal = async () => {
      if (!open) {
        return;
      }

      setIsLoading(true);
      try {
        const availableTags = await loadTags();

        if (!isMounted.current) {
          return;
        }

        setTags(availableTags);

        if (open === "edit") {
          const prevTagId = Number(data?.data?.tagId || 0);
          const matchedTag = availableTags.find(item => item.id === prevTagId);

          setTagAction(data?.data?.tagAction || "add");
          setSelectedTagId(matchedTag ? matchedTag.id : "");
          setMissingTagReference(Boolean(prevTagId && !matchedTag));
          setNewTagName(matchedTag ? "" : data?.data?.tagName || "");
        } else {
          setTagAction(initialState.tagAction);
          setSelectedTagId(initialState.tagId);
          setNewTagName("");
          setMissingTagReference(false);
        }

        setActiveModal(true);
      } catch (error) {
        toastError(error);
      } finally {
        if (isMounted.current) {
          setIsLoading(false);
        }
      }
    };

    bootstrapModal();

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const getRandomHexColor = () => {
    const red = Math.floor(Math.random() * 256);
    const green = Math.floor(Math.random() * 256);
    const blue = Math.floor(Math.random() * 256);

    return `#${red.toString(16).padStart(2, "0")}${green
      .toString(16)
      .padStart(2, "0")}${blue.toString(16).padStart(2, "0")}`;
  };

  const handleClose = () => {
    close(null);
    setActiveModal(false);
    setTags([]);
    setSelectedTagId("");
    setNewTagName("");
    setMissingTagReference(false);
  };

  const resolveTagToPersist = async () => {
    if (selectedTagId) {
      const selectedTag = tags.find(item => item.id === Number(selectedTagId));
      if (selectedTag) {
        return selectedTag;
      }
    }

    const normalizedName = String(newTagName || "").trim();
    if (!normalizedName) {
      return null;
    }

    if (normalizedName.length < 3) {
      toast.error("O nome da tag precisa ter no mínimo 3 caracteres.");
      return null;
    }

    const { data: createdTag } = await api.post("/tags", {
      name: normalizedName,
      kanban: 0,
      color: getRandomHexColor()
    });

    return createdTag;
  };

  const handleSaveTagNode = async () => {
    try {
      const tag = await resolveTagToPersist();

      if (!tag) {
        toast.error("Selecione uma tag existente ou crie uma nova.");
        return;
      }

      const payload = {
        tagAction,
        tagId: Number(tag.id),
        tagName: tag.name,
        tagColor: tag.color,
        onMissingTag: "skip"
      };

      if (open === "edit") {
        onUpdate({
          ...data,
          data: {
            ...data.data,
            ...payload
          }
        });
      } else {
        onSave(payload);
      }

      handleClose();
    } catch (error) {
      toastError(error);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
        <DialogTitle id="form-dialog-title">
          {open === "create" ? "Adicionar Tag ao fluxo" : "Editar Tag do fluxo"}
        </DialogTitle>

        <DialogContent dividers>
          <TextField
            select
            label="Ação"
            value={tagAction}
            onChange={event => setTagAction(event.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
          >
            {actionOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Selecionar tag existente"
            value={selectedTagId}
            onChange={event => {
              setSelectedTagId(event.target.value);
              if (event.target.value) {
                setNewTagName("");
              }
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            disabled={isLoading}
            helperText={
              missingTagReference
                ? "A tag anterior não existe mais. Selecione outra ou crie uma nova."
                : "Opcional: você também pode criar uma nova tag abaixo."
            }
          >
            <MenuItem value="">Nenhuma</MenuItem>
            {tags.map(tag => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label="Criar nova tag"
            value={newTagName}
            onChange={event => {
              setNewTagName(event.target.value);
              if (event.target.value) {
                setSelectedTagId("");
              }
            }}
            variant="outlined"
            margin="dense"
            fullWidth
            disabled={isLoading}
            placeholder="Digite o nome da nova tag"
          />

          <Typography variant="body2" style={{ marginTop: 8, color: "#666" }}>
            Quando o fluxo passar por este bloco, a tag será aplicada automaticamente ao contato.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            {i18n.t("contactModal.buttons.cancel")}
          </Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            className={classes.btnWrapper}
            onClick={handleSaveTagNode}
            disabled={isLoading}
          >
            {open === "create" ? "Adicionar" : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddTagModal;
